import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Progress from "@/components/Progress";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { withEmailPreviewCanvasPadding } from "./emailPreviewCanvasPadding";

export type EmailPreviewDialogProps = {
  open: boolean;
  onClosed: () => void;
  subject?: string;
  html?: string;
  loading?: boolean;
  errorMessage?: string | null;
  headline?: string;
};

function EmailPreviewContent({
  subject,
  html,
  loading,
  errorMessage
}: Pick<EmailPreviewDialogProps, "subject" | "html" | "loading" | "errorMessage">) {
  const { isCompactLayout } = useIsCompactLayout();
  const contentHeightClassName = isCompactLayout ? "h-[calc(100dvh-11rem)]" : "h-[calc(92vh-11rem)]";

  if (loading) {
    return (
      <div
        className={`flex ${contentHeightClassName} min-h-0 items-center justify-center`}
        data-testid="email-preview-loading"
      >
        <Progress />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <p className="text-body-medium text-sys-brand-error m-0 py-8" data-testid="email-preview-error">
        {errorMessage}
      </p>
    );
  }

  const previewHtml = withEmailPreviewCanvasPadding(html ?? "");

  return (
    <div className={`flex ${contentHeightClassName} min-h-0 flex-col gap-4`}>
      <div className="shrink-0 rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest px-4 py-3">
        <p className="text-body-medium text-sys-brand-on-surface m-0">
          <span className="font-semibold">Subject:</span>{" "}
          <span data-testid="email-preview-subject">{subject ?? ""}</span>
        </p>
      </div>
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-sys-brand-outline-variant bg-sys-brand-inverse-on-surface">
        <iframe
          title="Outbound email preview"
          srcDoc={previewHtml}
          sandbox=""
          className="block h-full w-full border-0 bg-sys-brand-inverse-on-surface"
          data-testid="email-preview-frame"
        />
      </div>
    </div>
  );
}

export default function EmailPreviewDialog({
  open,
  onClosed,
  subject,
  html,
  loading = false,
  errorMessage = null,
  headline = "Email Preview"
}: EmailPreviewDialogProps) {
  return (
    <Dialog
      open={open}
      onClosed={onClosed}
      headline={headline}
      hasFormElements
      className="w-[min(44rem,95vw)] max-w-[95vw] max-h-[92vh]"
      actions={
        <Button variant="filled" onClick={onClosed}>
          Close
        </Button>
      }
    >
      <EmailPreviewContent subject={subject} html={html} loading={loading} errorMessage={errorMessage} />
    </Dialog>
  );
}
